/**
 * Unified EVM wallet — MetaMask SDK (extension + mobile deep link + desktop QR relay)
 * with injected-provider fallback. Deep link pattern:
 *   https://metamask.app.link/dapp/{host}{pathname}{search}
 */

import type { MetaMaskSDK } from "@metamask/sdk";
import type { EvmChainDef } from "@/lib/bridgeNetworks";
import { dappAbsoluteUrl, metamaskDappLink } from "@/lib/walletDeepLinks";
import type { EthereumProvider } from "@/lib/evmWallet";

export type WalletConnector = "metamask-sdk" | "injected" | null;

export const ETH_MAINNET_CHAIN = {
  chainId: 1,
  hexChainId: "0x1" as const,
  name: "Ethereum Mainnet",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  rpcUrls: ["https://ethereum.publicnode.com"],
  blockExplorerUrls: ["https://etherscan.io"],
} satisfies EvmChainDef;

let sdk: MetaMaskSDK | null = null;
let sdkInit: Promise<MetaMaskSDK | null> | null = null;
let activeProvider: EthereumProvider | null = null;
let activeConnector: WalletConnector = null;
let activeAccount: string | null = null;

export function isMobileBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

/** MetaMask mobile deep link for the current or given path. */
export function getMetaMaskDeepLink(path?: string): string {
  return metamaskDappLink(path);
}

export async function getMetaMaskSdk(): Promise<MetaMaskSDK | null> {
  if (typeof window === "undefined") return null;
  if (sdk) return sdk;
  if (!sdkInit) {
    sdkInit = import("@metamask/sdk").then(({ MetaMaskSDK }) => {
      sdk = new MetaMaskSDK({
        dappMetadata: {
          name: "Aetherion Oracle",
          url: dappAbsoluteUrl("/"),
          iconUrl: `${dappAbsoluteUrl("/")}/favicon.ico`,
        },
        injectProvider: false,
        checkInstallationImmediately: false,
        preferDesktop: !isMobileBrowser(),
        useDeeplink: true,
        openDeeplink: (link: string) => {
          window.location.href = link;
        },
      });
      return sdk;
    });
  }
  return sdkInit;
}

export function getMetaMaskSdkSync(): MetaMaskSDK | null {
  return sdk;
}

export function getActiveConnector(): WalletConnector {
  return activeConnector;
}

export function getActiveAccount(): string | null {
  return activeAccount;
}

/** EIP-1193 provider: SDK session first, then injected extension. */
export function getActiveProvider(): EthereumProvider | null {
  if (activeProvider) return activeProvider;
  if (typeof window === "undefined") return null;
  const sdkProvider = getMetaMaskSdkSync()?.getProvider();
  if (sdkProvider) return sdkProvider as EthereumProvider;
  return window.ethereum ?? null;
}

function bindProviderEvents(provider: EthereumProvider): void {
  if (!provider.on) return;
  const onAccounts = (...args: unknown[]) => {
    const accs = args[0] as string[] | undefined;
    activeAccount = accs?.[0] ?? null;
    if (!activeAccount) {
      activeProvider = null;
      activeConnector = null;
    }
  };
  provider.on("accountsChanged", onAccounts);
  provider.on("disconnect", () => {
    activeAccount = null;
    activeProvider = null;
    activeConnector = null;
  });
}

export async function connectMetaMaskSdk(): Promise<string> {
  const instance = await getMetaMaskSdk();
  if (!instance) throw new Error("MetaMask SDK unavailable in this environment");
  const accounts = await instance.connect();
  const provider = instance.getProvider();
  if (!provider) throw new Error("MetaMask SDK returned no provider");
  activeProvider = provider as EthereumProvider;
  activeConnector = "metamask-sdk";
  bindProviderEvents(activeProvider);
  const addr = accounts?.[0];
  if (!addr) throw new Error("MetaMask returned no accounts");
  activeAccount = addr;
  return addr;
}

export async function connectInjectedWallet(): Promise<string> {
  if (typeof window === "undefined" || !window.ethereum) {
    throw new Error("No injected wallet found");
  }
  const accounts = (await window.ethereum.request({ method: "eth_requestAccounts" })) as string[];
  if (!accounts?.[0]) throw new Error("Wallet returned no accounts");
  activeProvider = window.ethereum;
  activeConnector = "injected";
  bindProviderEvents(activeProvider);
  activeAccount = accounts[0];
  return accounts[0];
}

export type ConnectWalletOptions = {
  /** Prefer MetaMask SDK (deep link / QR) over raw injected provider. Default true on mobile. */
  preferSdk?: boolean;
  /** Path segment for mobile deep link when no wallet is available. */
  deepLinkPath?: string;
};

/**
 * Connect wallet — MetaMask SDK primary (extension, mobile deep link, desktop QR),
 * injected Rainbow/MetaMask as fallback on desktop.
 */
export async function connectWallet(options: ConnectWalletOptions = {}): Promise<string> {
  const mobile = isMobileBrowser();
  const preferSdk = options.preferSdk ?? mobile;

  if (preferSdk) {
    try {
      return await connectMetaMaskSdk();
    } catch (err) {
      if (window.ethereum && !mobile) {
        try {
          return await connectInjectedWallet();
        } catch {
          /* fall through */
        }
      }
      throw err;
    }
  }

  if (window.ethereum) {
    return connectInjectedWallet();
  }

  if (mobile) {
    const path =
      options.deepLinkPath ??
      (typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search}`
        : "/bridge");
    window.location.href = getMetaMaskDeepLink(path);
    throw new Error("Opening MetaMask app…");
  }

  return connectMetaMaskSdk();
}

export async function disconnectWallet(): Promise<void> {
  const instance = getMetaMaskSdkSync();
  if (instance) {
    await instance.terminate().catch(() => {});
  }
  sdk = null;
  sdkInit = null;
  activeProvider = null;
  activeConnector = null;
  activeAccount = null;
}

export async function getWalletChainId(): Promise<number | null> {
  const eth = getActiveProvider();
  if (!eth) return null;
  const hex = (await eth.request({ method: "eth_chainId" })) as string;
  return Number.parseInt(hex, 16);
}

export async function switchOrAddChain(chain: EvmChainDef): Promise<void> {
  const eth = getActiveProvider();
  if (!eth) throw new Error("No wallet connected");
  try {
    await eth.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: chain.hexChainId }],
    });
  } catch (err) {
    const code = (err as { code?: number })?.code;
    if (code === 4902 || code === -32603) {
      await eth.request({
        method: "wallet_addEthereumChain",
        params: [
          {
            chainId: chain.hexChainId,
            chainName: chain.name,
            nativeCurrency: chain.nativeCurrency,
            rpcUrls: chain.rpcUrls,
            blockExplorerUrls: chain.blockExplorerUrls,
          },
        ],
      });
      return;
    }
    throw err;
  }
}

export async function switchToMainnet(): Promise<number> {
  await switchOrAddChain(ETH_MAINNET_CHAIN);
  const id = await getWalletChainId();
  if (id !== 1) throw new Error(`Expected mainnet (1), got ${id ?? "unknown"}`);
  return 1;
}

export function detectWalletLabel(provider: EthereumProvider | null = getActiveProvider()): string {
  if (!provider) return "No wallet";
  if (activeConnector === "metamask-sdk") return "MetaMask";
  if (provider.isRainbow) return "Rainbow";
  if (provider.isMetaMask) return "MetaMask";
  return "Injected wallet";
}

/** Restore session from an already-connected provider (page reload). */
export async function syncWalletSession(): Promise<string | null> {
  const eth = getActiveProvider();
  if (!eth) return null;
  try {
    const accounts = (await eth.request({ method: "eth_accounts" })) as string[];
    if (!accounts?.[0]) return null;
    activeAccount = accounts[0];
    activeProvider = eth;
    if (!activeConnector) {
      activeConnector =
        eth.isMetaMask || (await getMetaMaskSdk())?.isExtensionActive() ? "metamask-sdk" : "injected";
    }
    return accounts[0];
  } catch {
    return null;
  }
}
